import { useState } from 'react';
import { isThirdPartyScriptsEnabled } from '@gonfalon/dogfood-flags/dogfood-flags';
import { getGravatarUrl } from '@gonfalon/gravatar/getGravatarUrl';
import type { AvatarProps } from '@launchpad-ui/components';
import { Avatar } from '@launchpad-ui/components';
import clsx from 'clsx';

import styles from './MemberAvatar.module.css';

export interface MemberAvatarProps extends AvatarProps {
  email: string | undefined;
  initials: string | undefined;
  className?: string;
  ariaLabel?: string;
}

export function MemberAvatar({ email, initials, size, className, ariaLabel }: MemberAvatarProps) {
  const useGravatar = isThirdPartyScriptsEnabled();
  const [failedSrc, setFailedSrc] = useState<string>();

  let src = '';
  if (useGravatar && email !== undefined) {
    src = getGravatarUrl(email, {
      // Ask gravatar for a transparent placeholder when it has no image for the given email. Most
      // members have no gravatar account, and asking it to 404 instead reports a failed request per
      // member in the browser console. The placeholder is transparent, so the initials rendered
      // underneath remain the fallback.
      default: 'blank',
      size: 48,
    });
  }

  let finalInitials;
  if (initials) {
    finalInitials = initials.toUpperCase();
  } else if (email) {
    finalInitials = email.slice(0, 2).toUpperCase();
  } else {
    finalInitials = 'N/A';
  }

  // Avatar renders the initials as an <svg role="img"> that only receives
  // aria-label/aria-labelledby, never alt. Without a label here that SVG has no accessible name.
  const label = ariaLabel ?? (email ? `Avatar for ${email}` : 'Member avatar');

  return (
    <span className={clsx(styles.root, className)}>
      <Avatar alt={label} size={size} className={clsx('fs-exclude', 'meticulous-ignore')} aria-label={label}>
        {finalInitials}
      </Avatar>
      {src && src !== failedSrc ? (
        <img
          alt=""
          aria-hidden
          src={src}
          // Gravatar can be blocked, offline, or otherwise unreachable. Drop the overlay when it
          // fails so the browser's broken-image icon never covers the initials underneath.
          onError={() => setFailedSrc(src)}
          className={clsx(styles.gravatar, 'fs-exclude', 'meticulous-ignore')}
        />
      ) : null}
    </span>
  );
}
